import { useEffect, useMemo, useState } from "react";
import { EnvelopeSimple } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { inr, pct, fmtDate, gainClass, txnBadge, riskBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import WrmAvatar from "../../components/WrmAvatar";
import WithdrawalPanel from "../../components/WithdrawalPanel";
import StatCard from "../../components/ui/StatCard";
import SecureAvatar from "../../components/SecureAvatar";
import { AllocationDonut, GrowthArea } from "../../components/charts";
import { PeriodSelector, historyToChart } from "../../components/period";
import { Badge } from "../../components/ui/badge";

export default function InvestorDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("1Y");
  const [supportEmail, setSupportEmail] = useState("");

  useEffect(() => {
    api.get("/investor/dashboard").then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    api.get("/public/config").then((r) => setSupportEmail(r.data.support_email || "")).catch(() => {});
  }, []);

  const histData = useMemo(() => (data ? historyToChart(data.history, period) : []), [data, period]);

  if (error) return <AppLayout title="Overview"><p className="text-red-400 text-sm" data-testid="dashboard-error">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Overview"><PageLoader /></AppLayout>;

  const { metrics, profile, allocation, holdings, recent_transactions, wrm, withdrawal } = data;
  const firstName = profile.full_name.split(" ")[0];

  return (
    <AppLayout title="Overview">
      <div className="fade-up space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex items-center gap-4">
            <SecureAvatar investorId={profile.id} name={profile.full_name} size={56} testId="dashboard-avatar" />
            <div>
              <p className="label-caps mb-2">Private Wealth Portfolio</p>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-white" data-testid="greeting">
                Good day, {firstName}
              </h2>
              <div className="text-sm text-zinc-500 mt-2">
                {profile.investor_code} · Client since {fmtDate(profile.onboarding_date)}
                {wrm && (
                <span className="inline-flex items-center gap-1.5">
                  · <WrmAvatar userId={wrm.user_id} name={wrm.name} size={18} hasPhoto={wrm.has_photo} testId="dashboard-wrm-photo" /> Managed by {wrm.name}
                </span>
              )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`${riskBadge(profile.risk_profile)} text-[11px] px-3 py-1`}>
              {profile.risk_profile} Mandate
            </Badge>
            <Badge variant="outline" className="border-white/10 bg-white/[0.03] text-zinc-400 text-[11px] px-3 py-1">
              {profile.investment_plan}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="dashboard-stats">
          <StatCard label="Portfolio Value" value={inr(metrics.current_value)} testId="stat-current-value" />
          <StatCard label="Total Invested" value={inr(metrics.total_invested)} sub={`Since ${fmtDate(metrics.since)}`} testId="stat-total-invested" />
          <StatCard label="Total Gain / Loss" value={inr(metrics.gain_loss)} delta={metrics.gain_loss_pct} testId="stat-gain-loss" />
          <StatCard label="CAGR" value={metrics.cagr !== null ? pct(metrics.cagr) : "—"} sub="Annualised" testId="stat-cagr" />
          <StatCard label="XIRR" value={metrics.xirr !== null ? pct(metrics.xirr) : "—"} sub="Cash-flow adjusted" testId="stat-xirr" />
          <StatCard label="Total Withdrawals" value={inr(metrics.total_withdrawals || 0)}
            sub={`Capital ${inr(metrics.capital_withdrawn || 0)} · Returns ${inr(metrics.return_withdrawn || 0)}`}
            testId="stat-total-withdrawals" />
        </div>

        <WithdrawalPanel w={withdrawal} testId="inv-withdrawal" />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 glass-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h3 className="font-heading font-semibold text-white">Portfolio Performance</h3>
              <PeriodSelector period={period} setPeriod={setPeriod} testId="period-selector" />
            </div>
            {histData.length === 0 ? (
              <EmptyState message="Insufficient portfolio history available. Performance visualization will appear as valuation data is recorded." />
            ) : (
              <GrowthArea data={histData} testId="portfolio-growth-chart" investedKey="invested" />
            )}
          </div>
          <div className="lg:col-span-2 glass-card p-6">
            <h3 className="font-heading font-semibold text-white mb-4">Asset Allocation</h3>
            <AllocationDonut data={allocation} testId="asset-allocation-chart" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 glass-card overflow-hidden">
            <div className="px-6 pt-5 pb-4 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Top Holdings</h3>
              <a href="/holdings" className="text-xs text-emerald-400 font-medium hover:text-emerald-300 transition-colors duration-200" data-testid="view-all-holdings">View all</a>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="holdings-table">
              <thead>
                <tr className="border-y border-white/[0.06]">
                  <th className={thCls}>Asset / Fund</th>
                  <th className={thCls}>Class</th>
                  <th className={`${thCls} text-right`}>Current Value</th>
                  <th className={`${thCls} text-right`}>Return</th>
                </tr>
              </thead>
              <tbody>
                {holdings.slice(0, 5).map((h) => (
                  <tr key={h.id} className={rowCls}>
                    <td className={`${tdCls} font-medium text-white`}>{h.asset_name}</td>
                    <td className={`${tdCls} text-zinc-500 text-xs`}>{h.category}</td>
                    <td className={`${tdCls} text-right tnum text-zinc-200`}>{inr(h.current_value)}</td>
                    <td className={`${tdCls} text-right tnum font-semibold ${gainClass(h.return_pct)}`}>{pct(h.return_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          <div className="lg:col-span-2 glass-card overflow-hidden">
            <div className="px-6 pt-5 pb-4 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-white">Recent Transactions</h3>
              <a href="/transactions" className="text-xs text-emerald-400 font-medium hover:text-emerald-300 transition-colors duration-200" data-testid="view-all-transactions">View all</a>
            </div>
            <div data-testid="recent-transactions">
              {recent_transactions.map((t) => (
                <div key={t.id} className="px-6 py-3 border-b border-white/[0.04] last:border-0 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">{t.description}</p>
                    <p className="text-[11px] text-zinc-500">{fmtDate(t.date)} · {t.type}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="tnum text-sm font-medium text-zinc-200">{inr(t.amount)}</p>
                    <Badge variant="outline" className={`text-[10px] mt-0.5 ${txnBadge(t.status)}`}>{t.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {supportEmail && (
          <div className="glass-card p-6 flex flex-wrap items-center justify-between gap-4" data-testid="dashboard-support-card">
            <div>
              <p className="label-caps">Need Assistance?</p>
              <p className="text-sm text-zinc-400 mt-2">Our support team — <span className="tnum text-emerald-300" data-testid="dashboard-support-email">{supportEmail}</span></p>
            </div>
            <a href={`mailto:${supportEmail}?subject=KMC%20Investor%20Portal%20—%20Support%20Request`} data-testid="dashboard-contact-support"
              className="inline-flex items-center gap-2 text-xs font-bold rounded-lg px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-[#030706] emerald-glow transition-colors duration-200">
              <EnvelopeSimple size={15} weight="bold" /> Contact Support
            </a>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
